
'use client';

import { useEffect, useState, useCallback } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { db, auth } from '@/lib/firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import type { Product, ProductVariant, StoreSettings, User as AppUser, DaySchedule } from '@/lib/types';
import { Loader2, ShoppingCart, Check, Droplets } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/context/CartContext';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { getDay } from 'date-fns';
import NoWarrantyAlert from '@/components/shared/NoWarrantyAlert';
import { cn } from '@/lib/utils';

const NicotineStrengthIndicator = ({ strength }: { strength: number }) => {
    const totalBars = 5;
    const filledBars = Math.round(strength);

    const getColor = (index: number) => {
        if (index >= 4) return 'bg-red-500'; // 5%
        if (index >= 2) return 'bg-yellow-400'; // 3-4%
        return 'bg-green-500'; // 1-2%
    };

    return (
        <div className="space-y-2">
            <Label className="text-lg font-semibold">Força da Nicotina: {strength}%</Label>
            <div className="flex gap-1.5 items-center">
            {Array.from({ length: totalBars }).map((_, index) => (
                <div
                    key={index}
                    className={cn("h-3 w-6 rounded-sm", index < filledBars ? getColor(index) : 'bg-muted')}
                />
            ))}
            </div>
        </div>
    );
};

const VolumeIndicator = ({ ml }: { ml: number }) => {
    const getSize = () => {
        if (ml <= 30) return 'small';
        if (ml <= 60) return 'medium';
        return 'large';
    };
    const size = getSize();

    const iconClasses = "text-primary transition-all duration-300";
    const sizes = {
        small: <Droplets className={cn(iconClasses, "h-6 w-6")} />,
        medium: <Droplets className={cn(iconClasses, "h-8 w-8")} />,
        large: <Droplets className={cn(iconClasses, "h-10 w-10")} />,
    }

    return (
         <div className="space-y-2">
            <Label className="text-lg font-semibold">Volume: {ml}ml</Label>
            <div className="flex items-end gap-2">
                {sizes[size]}
            </div>
        </div>
    )
}

export default function ProductDetailClient({ product, storeSettings }: { product: Product, storeSettings: StoreSettings | null }) {
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const { addToCart } = useCart();
  const [showClosedStoreDialog, setShowClosedStoreDialog] = useState(false);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [showNoWarrantyAlert, setShowNoWarrantyAlert] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
            const userDocRef = doc(db, 'users', user.uid);
            const unsubUser = onSnapshot(userDocRef, (doc) => {
                if (doc.exists()) {
                    const userData = doc.data() as AppUser;
                    if (userData.isBlocked) {
                        signOut(auth).then(() => {
                            toast({
                                variant: 'destructive',
                                title: 'Acesso Bloqueado',
                                description: 'Sua conta foi bloqueada e você foi desconectado.'
                            });
                            router.push('/404');
                        });
                        return;
                    }
                    setAppUser(userData);
                } else {
                    signOut(auth);
                }
            });
            return () => unsubUser();
        } else {
            setAppUser(null);
        }
    });
    return () => unsubscribe();
  }, [router]);

  useEffect(() => {
    if (product.variants && product.variants.length > 0 && !selectedVariant) {
        setSelectedVariant(product.variants.find(v => (v.stock || 0) > 0) || product.variants[0]);
    }
  }, [product, selectedVariant]);
  
  const checkStoreOpen = () => {
    if (!storeSettings) return true;
    if (!storeSettings.isOpen) return false;
    
    const now = new Date();
    const dayOfWeekName = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"][getDay(now)] as DaySchedule['day'];
    const todaySchedule = storeSettings.schedule.find(s => s.day === dayOfWeekName);

    if (!todaySchedule || !todaySchedule.isOpen) return storeSettings.allowScheduled || false;

    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [openH, openM] = todaySchedule.openTime.split(':').map(Number);
    const [closeH, closeM] = todaySchedule.closeTime.split(':').map(Number);

    const openTime = openH * 60 + openM;
    const closeTime = closeH * 60 + closeM;

    const isOpenNow = closeTime < openTime 
        ? (currentTime >= openTime || currentTime <= closeTime)
        : (currentTime >= openTime && currentTime <= closeTime);

    return isOpenNow || storeSettings.allowScheduled;
  }

  const proceedAddToCart = (buyNow = false) => {
    if (!product) return;
    addToCart(product, quantity, selectedVariant || undefined);
    if (buyNow) router.push('/checkoutfast');
  }

  const handleAddToCart = async (buyNow = false) => {
    if (!product) return;
    if (product.variants && product.variants.length > 0 && !selectedVariant) {
        toast({ variant: 'destructive', title: "Selecione uma opção", description: "Você precisa escolher uma variação para comprar." });
        return;
    }

    if (!checkStoreOpen()) {
        setShowClosedStoreDialog(true);
        return;
    }

    if (product.noWarranty) {
      setShowNoWarrantyAlert(true);
      return;
    }
    
    proceedAddToCart(buyNow);
  };

  const handleConfirmAddToCart = () => {
    setShowClosedStoreDialog(false);
    proceedAddToCart();
  }

  const handleConfirmNoWarranty = () => {
    setShowNoWarrantyAlert(false);
    proceedAddToCart();
  };

  const getDisplayPriceInfo = useCallback(() => {
    const isVipUser = appUser?.isVip || false;

    if (selectedVariant) {
        const hasVipPrice = isVipUser && selectedVariant.vipPrice && selectedVariant.vipPrice > 0;
        const price = hasVipPrice ? selectedVariant.vipPrice! : selectedVariant.price;
        const originalPrice = hasVipPrice ? selectedVariant.price : selectedVariant.originalPrice;
        return { price, originalPrice };
    }
    
    if (product) {
        const hasVipPrice = isVipUser && product.vipPrice && product.vipPrice > 0;
        const price = hasVipPrice ? product.vipPrice! : product.price;
        const originalPrice = hasVipPrice ? product.price : product.originalPrice;
        return { price, originalPrice };
    }

    return { price: 0, originalPrice: undefined };
  }, [product, selectedVariant, appUser]);

  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
  const { price: displayPrice, originalPrice: displayOriginalPrice } = getDisplayPriceInfo();
  const displayStock = selectedVariant?.stock ?? product.stock ?? 0;
  const isOutOfStock = hasVariants
    ? product.variants!.every(v => (v.stock || 0) <= 0)
    : displayStock <= 0;

  const isCurrentVariantOutOfStock = hasVariants && (selectedVariant?.stock ?? 0) <= 0;
  const hasDiscount = displayOriginalPrice !== undefined && displayOriginalPrice > displayPrice;
  const optionKeys = hasVariants && product.variants && product.variants.length > 0 ? Object.keys(product.variants[0].options ?? {}) : [];

  return (
    <>
    <div className="container mx-auto max-w-6xl py-12 px-4 animate-in fade-in duration-700">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
        <div>
          <Image
            src={selectedVariant?.imageUrl || product.imageUrl || 'https://placehold.co/600x600.png'}
            alt={product.name}
            width={600}
            height={600}
            className="w-full rounded-lg shadow-lg object-cover aspect-square"
            priority
          />
        </div>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold font-headline leading-tight">{product.name}</h1>
            <div className="flex items-center gap-3">
                {product.brand && <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">{product.brand}</Badge>}
                {product.category && <Badge variant="outline">{product.category}</Badge>}
            </div>
          </div>

          <article className="prose prose-invert prose-sm max-w-none text-muted-foreground">
            {product.shortDescription}
          </article>

          <div className="flex items-baseline space-x-3">
            {hasDiscount ? (
              <>
                <p className="text-xl text-muted-foreground line-through decoration-destructive/50">De R$ {displayOriginalPrice!.toFixed(2)}</p>
                <p className="text-4xl font-bold text-primary">Por R$ {displayPrice.toFixed(2)}</p>
              </>
            ) : (
              <p className="text-4xl font-bold text-primary">R$ {displayPrice.toFixed(2)}</p>
            )}
          </div>
          
          <Separator className="opacity-20" />

          {product.nicotineStrength !== undefined && product.nicotineStrength > 0 && (
             <NicotineStrengthIndicator strength={product.nicotineStrength} />
          )}

          {product.ml !== undefined && product.ml > 0 && (
             <VolumeIndicator ml={product.ml} />
          )}

          {hasVariants && (
            <div className="space-y-4">
                {optionKeys.map(optionKey => (
                    <div key={optionKey} className="space-y-3">
                    <Label className="text-lg font-semibold capitalize">{optionKey}</Label>
                    <RadioGroup
                        value={selectedVariant?.id}
                        onValueChange={(variantId) => {
                            const newVariant = product.variants?.find(v => v.id === variantId) || null;
                            setSelectedVariant(newVariant);
                        }}
                        className="flex flex-wrap gap-3"
                    >
                        {product.variants!.map(variant => (
                            <Label
                                key={variant.id}
                                htmlFor={variant.id}
                                className={cn(
                                    "flex items-center justify-center p-3 border rounded-xl text-sm font-bold transition-all h-12 min-w-[100px]",
                                    (variant.stock || 0) <= 0 ? 'opacity-40 grayscale cursor-not-allowed' : 'cursor-pointer',
                                    selectedVariant?.id === variant.id ? 'border-primary bg-primary/5 ring-2 ring-primary shadow-lg shadow-primary/10' : 'hover:border-primary/50'
                                )}
                            >
                                <RadioGroupItem value={variant.id} id={variant.id} className="sr-only" disabled={(variant.stock || 0) <= 0} />
                                {variant.options[optionKey]}
                                {(variant.stock || 0) <= 0 && <span className="absolute -top-2 -right-2 bg-destructive text-white text-[8px] px-1 rounded-sm font-black">OFF</span>}
                            </Label>
                        ))}
                    </RadioGroup>
                    </div>
                ))}
            </div>
          )}

          {(isOutOfStock || isCurrentVariantOutOfStock) && (
             <Badge variant="destructive" className="text-lg px-4 py-1 font-black animate-pulse">PRODUTO ESGOTADO</Badge>
          )}

          <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
            <Button 
                size="lg" 
                className="w-full sm:flex-1 h-14 text-base font-bold shadow-xl shadow-primary/10" 
                onClick={() => handleAddToCart(false)} 
                disabled={isOutOfStock || isCurrentVariantOutOfStock}
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              ADICIONAR AO CARRINHO
            </Button>
            <Button 
                size="lg" 
                variant="outline" 
                className="w-full sm:flex-1 h-14 text-base font-bold border-primary text-primary hover:bg-primary/5" 
                onClick={() => handleAddToCart(true)} 
                disabled={isOutOfStock || isCurrentVariantOutOfStock}
            >
                COMPRAR AGORA
            </Button>
          </div>

          <Separator className="opacity-20" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-medium text-muted-foreground uppercase tracking-widest">
            <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <Check className="h-4 w-4 text-primary" /> 100% ORIGINAL
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <Check className="h-4 w-4 text-primary" /> ENTREGA EM ATÉ 60MIN
            </div>
          </div>
        </div>
      </div>
    </div>

    <AlertDialog open={showClosedStoreDialog} onOpenChange={setShowClosedStoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-headline">Loja Fechada</AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              Nossa loja está fechada no momento para envios imediatos, mas você pode agendar seu pedido para o próximo horário disponível e garantir seu estoque!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
             <AlertDialogCancel className="h-11">Voltar</AlertDialogCancel>
             <AlertDialogAction onClick={handleConfirmAddToCart} className="h-11 font-bold">Continuar e Agendar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NoWarrantyAlert 
        isOpen={showNoWarrantyAlert} 
        onClose={() => setShowNoWarrantyAlert(false)} 
        onConfirm={handleConfirmNoWarranty}
      />
    </>
  );
}
